import { Button, Form, Table, Tooltip } from 'antd';
import { usePurchaseRequestStore } from '../../../../../store/PurchaseRequestStore';
import React, { useState } from 'react';
import { useFetchData } from '../../../../../hooks';
import {
  BaseModel,
  FormField,
  PageableResponse,
  ProductModel,
  toProductMode,
} from '../../../../../types';
import { Resource } from '../../../../../lib/enums';
import { RequestLineForm } from '../../../../../types/PurchaseRequest';
import { DeleteOutlined, EditOutlined } from '@ant-design/icons';
import WarningModal from '../../../../../components/ui/modal/WarningModal';
import InfoModal from '../../../../../components/ui/modal/InfoModal';
import ModalForm from '../../../../../components/ui/ModalForm';
import dayjs from 'dayjs';
import { dateFormat } from '../../../../../lib/utils';

const RequestLineEditPage = () => {
  const [form] = Form.useForm();
  const purchaseRequestStore = usePurchaseRequestStore();
  const [modalVisible, setModalVisible] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);
  const [uuidDelete, setUuidDelete] = useState('');

  const { data: productData, isLoading: isProductLoading } = useFetchData<
    PageableResponse<ProductModel>
  >('/product/products', {
    resource: Resource.FULL,
    length: parseInt('999999'),
  });
  const { data: uomData, isLoading: isUomLoading } = useFetchData<
    PageableResponse<BaseModel>
  >('/product/uoms', {
    resource: Resource.MIN,
    search: '',
    length: '999999',
  });

  const fields: FormField[] = [
    {
      name: 'description',
      label: 'Description',
      type: 'input',
      defaultValue: purchaseRequestStore.requestLine?.description,
    },
    {
      name: 'product',
      label: 'Product',
      rules: [{ required: true, message: 'Please select product' }],
      isMultiple: false,
      type: 'select',
      isLoading: isProductLoading,
      values: productData?.data,
      defaultValue: purchaseRequestStore.requestLine?.product?.id,
    },
    {
      name: 'productUom',
      label: 'UoM',
      isMultiple: false,
      type: 'select',
      values: uomData?.data,
      isLoading: isUomLoading,
      defaultValue: purchaseRequestStore.requestLine?.productUom?.id,
    },
    {
      name: 'quantity',
      label: 'Quantity',
      type: 'numeric',
      rules: [{ required: true, message: 'Please enter quantity' }],
      defaultValue: purchaseRequestStore.requestLine?.quantity,
    },
    // --- specification sheet ------------------------------------------------
    // The workbook's NEW MATERIAL REQUEST FORM, per requested material. Left
    // empty for materials purchasing already knows; filled in when the request
    // is for something new, which is the case the form exists for.
    {
      name: 'specification',
      label: 'Material composition / thickness / kind',
      type: 'textarea',
      defaultValue: purchaseRequestStore.requestLine?.specification,
    },
    { name: 'size', label: 'Size', type: 'input', defaultValue: purchaseRequestStore.requestLine?.size },
    { name: 'length', label: 'Length', type: 'input', defaultValue: purchaseRequestStore.requestLine?.length },
    { name: 'width', label: 'Width', type: 'input', defaultValue: purchaseRequestStore.requestLine?.width },
    { name: 'height', label: 'Height', type: 'input', defaultValue: purchaseRequestStore.requestLine?.height },
    { name: 'diameter', label: 'Diameter', type: 'input', defaultValue: purchaseRequestStore.requestLine?.diameter },
    {
      name: 'circumference',
      label: 'Circumference',
      type: 'input',
      defaultValue: purchaseRequestStore.requestLine?.circumference,
    },
    { name: 'density', label: 'Density', type: 'input', defaultValue: purchaseRequestStore.requestLine?.density },
    { name: 'thickness', label: 'Thickness', type: 'input', defaultValue: purchaseRequestStore.requestLine?.thickness },
    {
      name: 'otherMeasurements',
      label: 'Other measurements',
      type: 'input',
      defaultValue: purchaseRequestStore.requestLine?.otherMeasurements,
    },
    {
      name: 'testsRequired',
      label: 'Tests / certificates required',
      type: 'textarea',
      defaultValue: purchaseRequestStore.requestLine?.testsRequired,
    },
    {
      name: 'purpose',
      label: 'Purpose / use of the material',
      type: 'textarea',
      defaultValue: purchaseRequestStore.requestLine?.purpose,
    },
    {
      name: 'sampleNeeded',
      label: 'Sample needed',
      type: 'switch',
      defaultValue: purchaseRequestStore.requestLine?.sampleNeeded,
    },
    {
      name: 'sampleDateNeeded',
      label: 'Sample date needed',
      type: 'datepicker',
      defaultValue: purchaseRequestStore.requestLine?.sampleDateNeeded,
    },
    {
      name: 'sampleQtyMinimum',
      label: 'Sample: exact minimum needed',
      type: 'numeric',
      defaultValue: purchaseRequestStore.requestLine?.sampleQtyMinimum,
    },
    {
      name: 'sampleQtyExcess',
      label: 'Sample: additional / excess',
      type: 'numeric',
      defaultValue: purchaseRequestStore.requestLine?.sampleQtyExcess,
    },
    {
      name: 'specialConsiderations',
      label: 'Special considerations',
      type: 'textarea',
      defaultValue: purchaseRequestStore.requestLine?.specialConsiderations,
    },
    {
      name: 'printingInstruction',
      label: 'Printing instruction',
      type: 'textarea',
      defaultValue: purchaseRequestStore.requestLine?.printingInstruction,
    },
    {
      name: 'supportMaterial',
      label: 'Other supporting material',
      type: 'textarea',
      defaultValue: purchaseRequestStore.requestLine?.supportMaterial,
    },
  ];

  const handleOpenModal = () => {
    setModalVisible(true);
    form.resetFields();
  };

  const handleCloseModal = () => {
    setModalVisible(false);
  };

  const handleEditButtonClick = (record: RequestLineForm) => {
    setModalVisible(true);
    purchaseRequestStore.setRequestLine(record);
  };
  const handleDeleteButtonClick = (record: RequestLineForm) => {
    setUuidDelete(record.uuid);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteModalClose = () => {
    setIsDeleteModalOpen(false);
  };

  const handleDeleteModalConfirm = () => {
    purchaseRequestStore.removeRequestLine(uuidDelete);
    setIsDeleteModalOpen(false);
    setIsInfoModalOpen(true);
  };

  const handleInfoModalClose = () => {
    setIsInfoModalOpen(false);
  };

  const onValuesChange = (changedFields: any) => {
    if (changedFields.product) {
      const product: ProductModel | undefined = productData?.data?.find(
        (obj) => {
          return obj.id == changedFields.product;
        },
      );
      form.setFieldValue('productUom', product?.defaultUom?.id);
    }
  };

  const onFinish = (value: any) => {
    let product: BaseModel | undefined = productData?.data.find((obj) => {
      return obj.id == value.product;
    });
    let productUom: BaseModel | undefined = uomData?.data.find((obj) => {
      return obj.id == value.productUom;
    });

    if (product === undefined) {
      const recProduct = purchaseRequestStore.formData.requestLines.map(
        (item) => toProductMode(item.product!),
      );

      product = recProduct.find((obj) => {
        return obj.id == value.product;
      });
    }

    if (productUom === undefined) {
      const recUoM = purchaseRequestStore.formData.requestLines.map(
        (item) => item.productUom!,
      );

      productUom = recUoM.find((obj) => {
        return obj.id == value.requestUom;
      });
    }

    const requestLine = {
      ...purchaseRequestStore.requestLine,
      ...value,
      product: product,
      productUom: productUom,
      // The picker hands back a dayjs object; the API wants a date string.
      sampleDateNeeded: value.sampleDateNeeded
        ? dayjs(value.sampleDateNeeded).format(dateFormat)
        : null,
      uuid:
        purchaseRequestStore?.requestLine?.uuid === undefined ||
        purchaseRequestStore?.requestLine?.uuid === null ||
        purchaseRequestStore?.requestLine?.uuid === ''
          ? ''
          : purchaseRequestStore.requestLine?.uuid,
    };
    if (
      requestLine.uuid === undefined ||
      requestLine.uuid === null ||
      requestLine.uuid === ''
    ) {
      purchaseRequestStore.addRequestLine(requestLine);
    } else {
      purchaseRequestStore.editRequestLine(requestLine.uuid, requestLine);
    }
    setModalVisible(false);
  };
  return (
    <>
      <div className="py-2 px-4 flex justify-between">
        <div className={'text-base font-semibold leading-6'}>Order Lines</div>
        <Button onClick={handleOpenModal}>Add Order</Button>
      </div>
      <div className="p-4">
        <Table
          rowKey={(record) => record.uuid}
          dataSource={purchaseRequestStore.formData.requestLines}
          pagination={{
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50', '100'],
          }}
        >
          <Table.Column title="Description" dataIndex="description" />
          <Table.Column
            title="Product"
            dataIndex="product"
            render={(_text: string, record: RequestLineForm) => {
              return <span>{record.product?.description}</span>;
            }}
          />
          <Table.Column
            title="Code"
            dataIndex="productCode"
            render={(_text: string, record: RequestLineForm) => {
              return <span>{record.product?.code}</span>;
            }}
          />
          <Table.Column
            title="Product UoM"
            dataIndex="productUoM"
            render={(_text: string, record: RequestLineForm) => {
              return <span>{record.productUom?.description}</span>;
            }}
          />
          <Table.Column title="Quantity" dataIndex="quantity" />
          <Table.Column
            title="Action"
            key="operation"
            fixed="right"
            render={(record: RequestLineForm) => {
              return (
                <div className="space-x-1">
                  <Tooltip title="Edit">
                    <Button
                      type="text"
                      icon={<EditOutlined style={{ color: '#4080fd' }} />}
                      size="middle"
                      onClick={() => handleEditButtonClick(record)}
                    />
                  </Tooltip>
                  <Tooltip title="Delete">
                    <Button
                      type="text"
                      icon={<DeleteOutlined />}
                      size="middle"
                      danger
                      onClick={() => handleDeleteButtonClick(record)}
                    />
                  </Tooltip>
                </div>
              );
            }}
          />
        </Table>
      </div>

      <WarningModal
        message={
          'This action cannot be undone and all associated data will be permanently removed from our system.'
        }
        onClose={handleDeleteModalClose}
        visible={isDeleteModalOpen}
        title={'Remove Order line item?'}
        onConfirm={handleDeleteModalConfirm}
      />
      <InfoModal
        visible={isInfoModalOpen}
        message={'Order line item successfully removed'}
        onClose={handleInfoModalClose}
      />

      <ModalForm
        visible={modalVisible}
        onClose={handleCloseModal}
        formFields={fields}
        rowCol={2}
        form={form}
        onFinish={onFinish}
        onValuesChange={onValuesChange}
        data={purchaseRequestStore.requestLine}
        setData={purchaseRequestStore.setRequestLine}
      />
    </>
  );
};

export default RequestLineEditPage;
